import React from "react"
import { connect } from "react-redux"
import trans from "trans"

import {
    GridCell,
    Grid,
    Message,
    InputGroup,
    Spinner,
} from "@bdfx/mergado-ui-kit"

import { initForm, updateInputValue as change, selectFormValue, selectFormValues } from "app/reducers/form"

import { getHelperContentByActiveView, setHelperText } from "./helper";

import {
    getFormName,
    recalculateShapes,
    setGuidesFromSizeOrRandomProduct,
} from "shape-edit/forms/helpers";

import { NumericInput } from "shape-edit/forms/fields";
import { AlternativeTemplate, Size, Template } from "app/types/model-types";
import SizeSelect from "../forms/size-select";
import { ID, ReduxList } from "app/types/app-types";
import { ComparisonEngineType } from "diagnostics/diagnostics";
import { RandomProduct } from "shape-edit/types";
import { projectId } from "app/helpers/api";
import { IconLightBulb } from "app/icons";
import { SizeItem } from "settings/components/sizes-settings";
import { ACTIVE_VIEW_EDIT_SOURCE_IMAGE } from "template-edit/containers/Editor";
import { debounce, find, random } from "lodash"
import { TemplateOrAlternativeTemplateType } from "template-edit/undoRedoReducers"
import { RENDERED_TYPE_OUTPAINTING } from "products/types"

type FormatType = 'preset' | 'original' | 'custom'

interface Props {
    isAlt: boolean
    form: TemplateOrAlternativeTemplateType
    sizes: ReduxList<SizeItem>
    initForm: typeof initForm
    change: typeof change
    randomProduct: RandomProduct
    selectedOutputComparisonEngine: ComparisonEngineType
}

interface State {
    showModal: boolean
    templates: any[]
    oldSizeBeforeNewFormat: Size
    customFormat: Size
    formatType: FormatType
}

class EditFormat extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            showModal: true,
            templates: [],
            oldSizeBeforeNewFormat: null,
            customFormat: {
                id: "new",
                project_id: projectId,
                width: props.form?.size?.width ? props.form.size.width : 1080,
                height: props.form?.size?.height ? props.form.size.height : 1080,
                name: trans('format_custom'),
            },
            formatType: this.getFormatType(props),
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps && prevProps.form?.size?.width && prevProps.form?.size?.height && this.props.form?.size?.width && this.props.form?.size?.height &&
            (prevProps.form?.size?.width !== this.props.form?.size?.width || prevProps.form?.size?.height !== this.props.form?.size?.height)
        ) {
            this.setState({customFormat: this.props.form.size})
        }
    }

    getFormatType = (props: Props): FormatType => {
        const { sizes, form, isAlt } = props

        const existingSize = find(sizes.data, (size: SizeItem) => {
            return `${size?.id}` === `${form.size_id}`
        })

        if (form.is_keep_size) {
            return 'original'
        } else if (!form.size_id || existingSize && existingSize.trans_string) {
            return 'preset'
        }

        return 'custom'
    }

    handleSizeChange = (newSizeId: ID) => {
        const { sizes, randomProduct, form, isAlt } = this.props
        const { customFormat } = this.state

        const formName = getFormName(isAlt)

        const oldSize: Size = form.size ?? {
            id: 'new',
            project_id: projectId,
            width: randomProduct?.width || 1080,
            height: randomProduct?.height || 1080,
            name: trans('format_custom'),
        }

        if (newSizeId === 'new') {
            if (customFormat.width > 0 && customFormat.height > 0) {
                this.props.initForm(formName, {
                    ...recalculateShapes(oldSize, customFormat, form),
                    is_keep_size: null
                })
                this.setState({ oldSizeBeforeNewFormat: oldSize })
                setGuidesFromSizeOrRandomProduct(customFormat, randomProduct)
            }
        } else {
            const existingSize = find(sizes.data, (size: SizeItem) => {
                return `${size?.id}` === `${newSizeId}`
            })

            if (existingSize && existingSize.id) {
                const newSize: Size = {
                    id: existingSize.id,
                    project_id: existingSize.project_id,
                    width: existingSize.width,
                    height: existingSize.height,
                    name: existingSize.name || trans(existingSize.trans_string),
                }

                this.props.initForm(formName, {
                    ...recalculateShapes(oldSize, newSize, form),
                    is_keep_size: null
                })
                this.setState({ oldSizeBeforeNewFormat: oldSize })
                setGuidesFromSizeOrRandomProduct(newSize, randomProduct)
            }
        }

        setHelperText(getHelperContentByActiveView(ACTIVE_VIEW_EDIT_SOURCE_IMAGE,
            isAlt ?
                { altTemplateForm: { size_id: 'selected' } } :
                { templateForm: { size_id: 'selected' } }
        ))
    }

    recalculateUsingOldSizeBeforeNewDebounced = debounce(() => {
        const { oldSizeBeforeNewFormat } = this.state
        const { randomProduct, form, isAlt } = this.props

        const formName = getFormName(isAlt)

        const MIN_SIDE = 10

        const newSize = {
            ...form.size,
            width: form.size.width && form.size.width < MIN_SIDE ? MIN_SIDE : form.size.width,
            height: form.size.height && form.size.height < MIN_SIDE ? MIN_SIDE : form.size.height,
        }

        this.props.initForm(formName, recalculateShapes(oldSizeBeforeNewFormat, newSize, form))
        this.setState({ oldSizeBeforeNewFormat: oldSizeBeforeNewFormat })
        setGuidesFromSizeOrRandomProduct(newSize, randomProduct)
    }, 2000)

    handleFormatType = (value: FormatType) => {
        const { randomProduct, form, isAlt } = this.props
        const { oldSizeBeforeNewFormat, customFormat } = this.state

        const oldFormat = { ...form.size }
        const formName = getFormName(isAlt)

        this.setState({
            formatType: value,
            oldSizeBeforeNewFormat: oldFormat    //save old format before change
        })

        switch (value) {
            case 'original':
                this.props.initForm(formName, {
                    ...form,
                    //size_id: null,    //do not reset size_id, if user suddenly decide to change,
                    //size: null,       //the layers appears back again but without history of their original size !!!
                    is_keep_size: 1,    //check all joins and avoid unnecessary joins to size table (search for size_id)
                    type: form.type === RENDERED_TYPE_OUTPAINTING ? null : form.type,
                })
                break;
            case 'custom':
                if (!oldSizeBeforeNewFormat) {
                    this.props.initForm(formName, {
                        ...form,
                        size_id: customFormat.id,
                        size: customFormat,
                        is_keep_size: null,
                    })
                } else {
                    const r = recalculateShapes(oldSizeBeforeNewFormat, customFormat, form)
                    this.props.initForm(formName, {
                        ...form,
                        ...r,
                        size_id: r.size_id ? r.size_id : customFormat.name,
                        size: r.size ? r.size : customFormat,
                        is_keep_size: null,
                    })
                    setGuidesFromSizeOrRandomProduct(customFormat, randomProduct)
                }
                break;
            default:
                if (oldSizeBeforeNewFormat && form.size_id === 'new') {     //return back from the new format to pre-defined:
                    const r = recalculateShapes(form.size, oldSizeBeforeNewFormat, form)
                    this.props.initForm(formName, {
                        ...form,
                        ...r,
                        size_id: r.size_id ? r.size_id : oldSizeBeforeNewFormat.id,
                        size: r.size ? r.size : oldSizeBeforeNewFormat,
                        is_keep_size: null,
                    })
                    setGuidesFromSizeOrRandomProduct(form.size, randomProduct)
                } else if (form.is_keep_size) {
                    this.props.change(formName, 'is_keep_size', null)
                }
        }
    }

    render() {
        const { sizes, selectedOutputComparisonEngine, form, isAlt } = this.props
        const { formatType, customFormat } = this.state

        const formName = getFormName(isAlt)

        if (!sizes.isLoaded) {
            return <GridCell cols={'1/-1'}>
                <Spinner type='bubbles' />
            </GridCell>
        }

        return <React.Fragment>
            <GridCell cols={'1/-1'} style={{ marginBottom: '20px' }}>
                <InputGroup
                    orientation="horizontal"
                    onChange={this.handleFormatType}
                    value={formatType}
                    singleChoice={true}
                    hideInput={true}
                    items={[
                        { label: trans('format_preset'), value: 'preset' },
                        { label: trans('format_original'), value: "original" },
                        { label: trans('format_custom'), value: "custom" },
                    ]}
                />
            </GridCell>

            {formatType === 'original' &&
                <GridCell cols={'1/-1'}>
                    <Message type={"warning"} closeable={false} icon={<IconLightBulb size={24} />} style={{ borderColor: '#e0bf00' }}>
                        {trans('is_keep_size_info')}
                    </Message>
                </GridCell>
            }

            {formatType === 'custom' &&
                <React.Fragment>
                    {/* <GridCell style={{ marginTop: '10px' }}>{trans("format_name")}</GridCell>
                    <GridCell>
                        <TextInput
                            name={`${formName}.size.name`}
                            onChange={(name: string) => {
                                this.setState({customFormat: {...customFormat, name: name}})
                            }}
                        />
                    </GridCell> */}

                    <GridCell style={{ marginTop: '10px' }}>{trans('shape_size')}</GridCell>

                    <Grid cols="1fr 1fr">
                        <GridCell style={{ position: 'relative' }}>
                            <NumericInput
                                formName={formName}
                                fieldName={'size.width'}
                                placeholder={trans('width')}
                                id={'size-width'}
                                step={10}
                                value={customFormat ? customFormat.width : form.size?.width}
                                onChangeFunction={(value: number) => {
                                    this.setState({customFormat: {...customFormat, width: value}})
                                    this.recalculateUsingOldSizeBeforeNewDebounced()
                                }}
                            />
                        </GridCell>
                        <GridCell style={{ position: 'relative' }}>
                            <NumericInput
                                formName={formName}
                                fieldName={'size.height'}
                                placeholder={trans('height')}
                                id={'size-height'}
                                step={10}
                                value={customFormat ? customFormat.height : form.size?.height}
                                onChangeFunction={(value: number) => {
                                    this.setState({customFormat: {...customFormat, height: value}})
                                    this.recalculateUsingOldSizeBeforeNewDebounced()
                                }}
                            />
                        </GridCell>
                    </Grid>
                </React.Fragment>
            }

            {(formatType === 'preset'  || formatType === 'custom') &&
                <GridCell cols={'1/-1'} style={{ marginTop: '20px' }}>
                    <SizeSelect
                        customOnly={formatType === 'custom'}
                        placeholder={trans('find_format')}
                        showInput={false}
                        selectedSizeId={`${form.size_id}`}
                        handleSelectId={this.handleSizeChange}
                        filter={selectedOutputComparisonEngine?.name ? selectedOutputComparisonEngine.name : ''}
                    />
                </GridCell>
            }
        </React.Fragment>
    }
}

function mapStateToProps(state) {
    return {
        isAlt: state.isAlt,
        form: selectFormValues(state, getFormName(state.isAlt)),
        randomProduct: state.randomProduct,
        sizes: state.sizes,
        selectedOutputComparisonEngine: state.selectedOutputComparisonEngine,
    }
}

export default connect(mapStateToProps, {
    initForm,
    change,
})(EditFormat)